// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/** A carried assertion — a verdict the user's own eval harness recorded, not one we derived. */
export interface CarriedAssertion {
  /** promptfoo `assertion.type`, OpenAI `passes` key, oaieval `match`. */
  readonly type: string;
  /** Stable-stringified `assertion.value`, or '' when the dialect carries none. */
  readonly value: string;
  /**
   * Ordinal WITHIN (type, value). promptfoo routinely emits several assertions of one type; keying
   * on type alone reports a fabricated flip whenever two of them swap outcomes.
   */
  readonly ordinal: number;
  readonly pass: boolean;
  readonly reason?: string;
}

/** One tool call in a trajectory, before normalisation. */
export interface ToolCall {
  readonly name: string;
  readonly args: unknown;
}

/**
 * One physical record from one file.
 *
 * `expected`, `finishReason`, `statusCode` and `cached` are on the interface from the start, not
 * retrofitted: three scorers are unimplementable without them, and `cached` is what stops a cached
 * run (latencyMs: 0) reporting a latency regression on 100% of rows.
 */
export interface Row {
  /** Stable composite key from the dialect, US-joined. Empty string when the dialect has none. */
  readonly key: string;
  /** Ordinal fanout identity — NEVER the prompt text, which changes between runs by construction. */
  readonly fanoutId: string;
  /** Human label for the fanout. Display only. */
  readonly fanoutLabel: string;
  readonly inputText: string;
  readonly outputText: string;
  /** True when `outputText` came from stringifying an object we parsed — `is-json` can never flip. */
  readonly outputWasSerialized: boolean;
  readonly expected: string | null;
  readonly carried: readonly CarriedAssertion[];
  readonly toolCalls: readonly ToolCall[];
  readonly latencyMs: number | null;
  readonly cost: number | null;
  readonly tokensIn: number | null;
  readonly tokensOut: number | null;
  readonly cached: boolean;
  readonly finishReason: string | null;
  readonly statusCode: number | null;
  readonly error: string | null;
  /** Verbatim source text for this record, so strict passthrough is byte-exact. */
  readonly rawText: string;
  readonly index: number;
}

export type DialectId =
  | 'promptfoo-json'
  | 'promptfoo-jsonl'
  | 'openai-batch'
  | 'openai-evals-dashboard'
  | 'oaieval'
  | 'raw-completions'
  | 'generic-jsonl'
  | 'csv';

export interface FieldMap {
  key: string | null;
  output: string | null;
  expected: string | null;
}

export interface ParsedSide {
  readonly dialect: DialectId;
  readonly framing: Framing;
  readonly rows: readonly Row[];
  readonly fanouts: readonly { id: string; label: string; count: number }[];
  readonly notes: readonly string[];
  /** Only set for generic/CSV dialects. */
  readonly fieldMap?: FieldMap;
  readonly candidatePaths?: readonly string[];
}

export type Framing = 'object' | 'array' | 'jsonl' | 'csv';

export type JoinMethod =
  | 'exact-key'
  | 'content-hash'
  | 'affix-hash'
  | 'lastparagraph-hash'
  | 'tail-hash'
  | 'similarity'
  | 'ambiguous-group'
  | 'unmatched';

export type JoinConfidence = 'exact' | 'high' | 'ambiguous';

export interface Pair {
  readonly a: Row | null;
  readonly b: Row | null;
  readonly method: JoinMethod;
  readonly confidence: JoinConfidence;
  readonly score: number | null;
  /** Non-null when this pair sits inside a group of indistinguishable inputs. */
  readonly ambiguousGroupSize: number | null;
}

export interface JoinResult {
  readonly pairs: readonly Pair[];
  /** Which key function won, for the provenance line. */
  readonly keyFunction: string;
  readonly exact: number;
  readonly similarity: number;
  readonly ambiguous: number;
  readonly dropped: number;
  readonly added: number;
  readonly notes: readonly string[];
}

/**
 * `pass`/`fail` are the only values that can flip. `na` means the scorer structurally could not
 * look — which is a different statement from "it looked and found nothing wrong", and conflating
 * the two is how a tool reports "0 rows got worse" about a file it could not read.
 */
export type ScoreValue = 'pass' | 'fail' | 'na';

export type ScorerClass = 'verdict' | 'change';

export interface ScorerResult {
  readonly scorer: string;
  readonly a: ScoreValue;
  readonly b: ScoreValue;
  /** true only when a === 'pass' && b === 'fail'. */
  readonly flipped: boolean;
  /** true only when a === 'fail' && b === 'pass'. */
  readonly fixed: boolean;
  readonly detail: string;
}

export type Verdict =
  | 'regression'
  | 'errored'
  | 'diverged'
  | 'diverged-args'
  | 'fixed'
  | 'unchanged'
  | 'dropped'
  | 'added'
  | 'unmatched'
  | 'ambiguous-group';

export interface TrajectorySummary {
  readonly alignment: string;
  readonly exact: number;
  readonly argsDiffer: number;
  readonly mismatched: number;
  readonly inserted: number;
  readonly deleted: number;
  readonly insertedBlocks: number;
  readonly deletedBlocks: number;
  readonly firstDivergent: number | null;
  readonly stepsA: number;
  readonly stepsB: number;
}

export interface CaseResult {
  readonly id: string;
  readonly fanoutId: string;
  readonly fanoutLabel: string;
  readonly verdict: Verdict;
  readonly headlineScorer: string | null;
  readonly headlineReason: string;
  readonly scores: readonly ScorerResult[];
  readonly changes: readonly ScorerResult[];
  readonly trajectory: TrajectorySummary | null;
  readonly pair: Pair;
  readonly editDistance: number | null;
}

export interface Summary {
  readonly cases: number;
  readonly regressed: number;
  readonly errored: number;
  readonly diverged: number;
  readonly fixed: number;
  readonly unchanged: number;
  readonly dropped: number;
  readonly added: number;
  readonly unmatched: number;
  readonly ambiguousGroups: number;
  readonly ambiguousRows: number;
  readonly ambiguousNetDelta: number;
  /** Precedence-resolved: each case contributes to at most one scorer. Sums to `regressed`. */
  readonly flipsByScorer: Readonly<Record<string, number>>;
  readonly fixesByScorer: Readonly<Record<string, number>>;
  /** Scorers that could not fire on ANY row, with why. Drives the capability check. */
  readonly inapplicable: readonly { scorer: string; why: string }[];
  readonly join: {
    readonly keyFunction: string;
    readonly method: string;
    readonly exact: number;
    readonly similarity: number;
    readonly ambiguous: number;
  };
  readonly notes: readonly string[];
}

export interface NormaliseRules {
  sortObjectKeys: boolean;
  maskUuid: boolean;
  maskIso8601: boolean;
  maskEpochMs: boolean;
  maskPrefixedId: boolean;
  roundFloat: boolean;
  floatDigits: number;
  dropKeys: string[];
  sortArrays: boolean;
  maskLongHex: boolean;
}

export interface Thresholds {
  latencyRatio: number;
  latencyFloorMs: number;
  costRatio: number;
  costFloor: number;
  lengthMin: number | null;
  lengthMax: number | null;
}

export interface Config {
  readonly rules: NormaliseRules;
  readonly thresholds: Thresholds;
  readonly schema: string | null;
  readonly enabled: Readonly<Record<string, boolean>>;
  readonly fanoutFilter: readonly string[] | null;
  readonly fieldMap: FieldMap | null;
}

export const DEFAULT_RULES: NormaliseRules = {
  sortObjectKeys: true,
  maskUuid: true,
  maskIso8601: true,
  maskEpochMs: true,
  maskPrefixedId: true,
  roundFloat: true,
  floatDigits: 4,
  // The sharpest edge in the tool. Adding a key here can silently hide a real downgrade, so the
  // default list is short and obviously volatile, and every addition raises a visible warning.
  dropKeys: ['request_id', 'requestId', 'timestamp', 'idempotency_key', 'session_id'],
  // OFF. It is the one rule that can hide a real bug with no compensating benefit:
  // ["refund","damage"] vs ["damage","refund"] is noise for `tags` and a genuine bug for `steps`.
  sortArrays: false,
  maskLongHex: false,
};

export const DEFAULT_THRESHOLDS: Thresholds = {
  latencyRatio: 2,
  latencyFloorMs: 250,
  costRatio: 2,
  // NOT zero. `delta >= 0` is a tautology, which would make every B > A a "regression".
  costFloor: 0.0005,
  lengthMin: null,
  lengthMax: null,
};

/** Hard limits, stated in preflight before anything runs rather than discovered as a hang. */
export const LIMITS = {
  softRows: 20_000,
  hardRows: 40_000,
  hardBytes: 128 * 1024 * 1024,
  /** Stage 4 is O(n·m). Above this it refuses rather than spending 50 seconds. */
  maxSimilarityResidual: 1_500,
  /** Regex scorers refuse longer inputs — catastrophic backtracking needs no length to bite. */
  maxRegexInput: 4_000,
} as const;
